/* global Stripe */
import PropTypes from 'prop-types'
import {useState, useEffect, useRef} from 'react'
import {connect} from 'react-redux'
import {createResource, readEndpoint} from 'redux-json-api'

import SecurePaymentNotice from '../../../../shared_components/SecurePaymentNotice'
import Button from '../../../../shared_components/mui_base_components/button/Button'

import {
  createResourcePayload,
  formatCouponValidationResponse,
  formatPaymentIntentResponse
} from './preneedPaymentApi'

import './preneedPaymentForm.scss'

const ELEMENT_STYLES = {
  style: {
    base: {
      color: '#262626',
      fontFamily: 'proxima-nova, Helvetica, Arial, sans-serif',
      fontSize: '20px',
      '::placeholder': {
        color: '#A8A8A8',
        lineHeight: 'normal'
      }
    },
    invalid: {
      color: '#D10303'
    }
  }
}

export const PreneedPaymentFormComponent = ({onSuccess, createResource: createResourceProp, readEndpoint: readEndpointProp}) => {
  // State
  const [couponCode, setCouponCode] = useState('')
  const [couponApplied, setCouponApplied] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [processingPayment, setProcessingPayment] = useState(false)
  const [validatingCoupon, setValidatingCoupon] = useState(false)
  const [stripeLoaded, setStripeLoaded] = useState(false)

  // Refs
  const stripeRef = useRef(null)
  const elementsRef = useRef(null)
  const cardNumberElementRef = useRef(null)
  const cardExpiryElementRef = useRef(null)
  const cardCvcElementRef = useRef(null)
  const stripeCheckIntervalRef = useRef(null)
  const paymentPollingIntervalRef = useRef(null)

  // Initialize Stripe
  const initializeStripe = () => {
    stripeRef.current = Stripe(STRIPE_PUBLISHABLE_KEY) // eslint-disable-line babel/new-cap
    elementsRef.current = stripeRef.current.elements()

    // Create card number element with Link explicitly disabled
    const cardNumberOptions = {
      ...ELEMENT_STYLES,
      disableLink: true
    }

    cardNumberElementRef.current = elementsRef.current.create('cardNumber', cardNumberOptions)
    cardExpiryElementRef.current = elementsRef.current.create('cardExpiry', ELEMENT_STYLES)
    cardCvcElementRef.current = elementsRef.current.create('cardCvc', ELEMENT_STYLES)

    // Mount elements to their containers
    cardNumberElementRef.current.mount('#card-number-element')
    cardExpiryElementRef.current.mount('#card-expiry-element')
    cardCvcElementRef.current.mount('#card-cvc-element')

    const attachFocusHandlers = (element, wrapperId) => {
      const wrapper = document.getElementById(wrapperId)

      if (!element || !wrapper) return

      element.on('focus', () => wrapper.classList.add('focused'))
      element.on('blur', () => wrapper.classList.remove('focused'))
    }

    attachFocusHandlers(cardNumberElementRef.current, 'card-number-element')
    attachFocusHandlers(cardExpiryElementRef.current, 'card-expiry-element')
    attachFocusHandlers(cardCvcElementRef.current, 'card-cvc-element')

    setStripeLoaded(true)
  }

  // Effect: Initialize Stripe on mount
  useEffect(() => {
    // Initialize Stripe.js v3 - Poll for it to become available if not loaded yet
    if (typeof Stripe !== 'undefined') {
      initializeStripe()
    } else {
      // Poll for Stripe to become available (in case script is still loading)
      stripeCheckIntervalRef.current = setInterval(() => {
        if (typeof Stripe !== 'undefined') {
          clearInterval(stripeCheckIntervalRef.current)
          initializeStripe()
        }
      }, 100)
    }

    // Cleanup on unmount
    return () => {
      // Clean up interval if component unmounts before Stripe loads
      if (stripeCheckIntervalRef.current)
        clearInterval(stripeCheckIntervalRef.current)

      // Clean up payment polling interval
      if (paymentPollingIntervalRef.current)
        clearInterval(paymentPollingIntervalRef.current)

      // Clean up Stripe elements
      if (cardNumberElementRef.current)
        cardNumberElementRef.current.unmount()
      if (cardExpiryElementRef.current)
        cardExpiryElementRef.current.unmount()
      if (cardCvcElementRef.current)
        cardCvcElementRef.current.unmount()
    }
  }, [])

  // Handle coupon application
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setErrorMessage('Please enter a coupon code')
      return
    }

    setValidatingCoupon(true)
    setErrorMessage('')
    setSuccessMessage('')

    try {
      const resource = createResourcePayload('preneed-client-coupon-validations', {
        'coupon-code': couponCode.trim()
      })

      const response = await createResourceProp(resource)
      const result = formatCouponValidationResponse(response)

      if (result.valid) {
        setCouponApplied(result)
        setSuccessMessage(
          `Coupon applied! You save $${(result.discountAmount / 100).toFixed(2)}. ` +
          `New total: $${(result.finalAmount / 100).toFixed(2)}`
        )
      } else {
        setErrorMessage(result.errorMessage || 'Invalid coupon code')
        setCouponApplied(null)
      }
    } catch (error) {
      setErrorMessage('Error validating coupon code')
      setCouponApplied(null)
    } finally {
      setValidatingCoupon(false)
    }
  }

  // Handle coupon removal
  const handleRemoveCoupon = () => {
    setCouponCode('')
    setCouponApplied(null)
    setSuccessMessage('')
    setErrorMessage('')
  }

  // Poll payment status
  const pollPaymentStatus = paymentIntentId => {
    const MAX_POLLING_ATTEMPTS = 15 // Poll for up to 15 seconds
    const POLLING_INTERVAL = 1000 // Poll every second
    let attempts = 0

    return new Promise((resolve, reject) => {
      paymentPollingIntervalRef.current = setInterval(async () => {
        attempts++

        try {
          const response = await readEndpointProp('sales-studio-stripe-one-time-payments')
          const responseBody = await response.body

          if (responseBody && responseBody.data && responseBody.data.length > 0) {
            const payment = responseBody.data.find(
              paymentRecord => paymentRecord.attributes['stripe-payment-intent-id'] === paymentIntentId
            )

            if (payment) {
              clearInterval(paymentPollingIntervalRef.current)
              paymentPollingIntervalRef.current = null

              if (payment.attributes.status === 'succeeded')
                resolve({success: true, paymentId: payment.id})
              else if (payment.attributes.status === 'failed')
                reject(new Error(payment.attributes['error-message'] || 'Payment failed'))
              else if (attempts >= MAX_POLLING_ATTEMPTS)
                resolve({success: true, delayed: true})

              return
            }
          }

          if (attempts >= MAX_POLLING_ATTEMPTS) {
            clearInterval(paymentPollingIntervalRef.current)
            paymentPollingIntervalRef.current = null
            resolve({success: true, delayed: true})
          }
        } catch (error) {
          if (attempts >= MAX_POLLING_ATTEMPTS) {
            clearInterval(paymentPollingIntervalRef.current)
            paymentPollingIntervalRef.current = null
            resolve({success: true, delayed: true})
          }
        }
      }, POLLING_INTERVAL)
    })
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!stripeRef.current || !cardNumberElementRef.current)
      return

    setProcessingPayment(true)
    setErrorMessage('')
    setSuccessMessage('')

    try {
      const paymentIntentPayload = createResourcePayload('preneed-client-stripe-payment-intents', {
        ...(couponApplied && {'coupon-code': couponCode.trim()})
      })

      const paymentIntentResponse = await createResourceProp(paymentIntentPayload)
      const paymentIntent = formatPaymentIntentResponse(paymentIntentResponse)

      const {error, paymentIntent: confirmedIntent} = await stripeRef.current.confirmCardPayment(
        paymentIntent.clientSecret,
        {
          payment_method: {
            card: cardNumberElementRef.current
          }
        }
      )

      if (error) {
        setErrorMessage(error.message)
        setProcessingPayment(false)
      } else if (confirmedIntent.status === 'succeeded') {
        setSuccessMessage('Payment confirmed! Finalizing...')

        try {
          const result = await pollPaymentStatus(confirmedIntent.id)

          setSuccessMessage('Payment successful!')
          setProcessingPayment(false)

          setTimeout(() => {
            onSuccess(result.paymentId)
          }, 1500)
        } catch (pollError) {
          setErrorMessage(pollError.message || 'Payment verification failed. Please contact support.')
          setProcessingPayment(false)
        }
      } else {
        setErrorMessage('Payment failed. Please try again.')
        setProcessingPayment(false)
      }
    } catch (err) {
      setErrorMessage(err.message || 'An error occurred. Please try again.')
      setProcessingPayment(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className='preneed-payment-form'>
      <div className='preneed-payment-form__coupon-section'>
        <label htmlFor='coupon'>Have a coupon code?</label>
        <div className='preneed-payment-form__coupon-input-group'>
          <input
            id='coupon'
            type='text'
            value={couponCode}
            onChange={event => setCouponCode(event.target.value.toUpperCase())}
            placeholder='Enter here'
            className='preneed-payment-form__input'
          />
          {!couponApplied ?
            (
              <button
                onClick={handleApplyCoupon}
                disabled={
                  !couponCode.trim() ||
                  validatingCoupon ||
                  processingPayment
                }
                className='preneed-payment-form__apply-button'
                type='button'>
                {validatingCoupon ? 'Validating...' : 'Apply'}
              </button>
            ) :
            (
              <button
                onClick={handleRemoveCoupon}
                disabled={processingPayment}
                className='preneed-payment-form__remove-button'
                type='button'>
              Remove
              </button>
            )}
        </div>
      </div>
      <div className='preneed-payment-form__credit-card-section'>
        <div className='preneed-payment-form__field'>
          <label htmlFor='card-number-element'>Credit card number</label>
          <div id='card-number-element' className='preneed-payment-form__card-element' data-form-type='other' />
        </div>

        <div className='preneed-payment-form__row'>
          <div className='preneed-payment-form__field preneed-payment-form__field--half'>
            <label htmlFor='card-expiry-element'>Exp. date</label>
            <div id='card-expiry-element' className='preneed-payment-form__card-element' data-form-type='other' />
          </div>
          <div className='preneed-payment-form__field preneed-payment-form__field--half'>
            <label htmlFor='card-cvc-element'>CVC</label>
            <div id='card-cvc-element' className='preneed-payment-form__card-element' data-form-type='other' />
          </div>
        </div>

        {errorMessage && (
          <div className='preneed-payment-form__error'>
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div className='preneed-payment-form__success'>
            {successMessage}
          </div>
        )}

        <div className='button-wrapper'>
          <Button
            onClick={handleSubmit}
            disabled={!stripeLoaded || processingPayment}
            className='preneed-payment-form__submit-button'
            loading={processingPayment}>
            Upgrade now
          </Button>
        </div>
      </div>
      <SecurePaymentNotice />
    </form>
  )
}

PreneedPaymentFormComponent.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  createResource: PropTypes.func.isRequired,
  readEndpoint: PropTypes.func
}

export default connect(null, {createResource, readEndpoint})(PreneedPaymentFormComponent)
